import { fetchWrapper_v2 } from 'src/utils/api_v2';
import { getProductScope } from '@discover-frontend/utils';
import { getAuthHeadersWithBearer } from 'src/utils/ApiRequestHeader';
import { fetchMembershipApi } from 'src/services/engineApi';

import { STANLEY_QUERY_BUILDER_URL } from './constants';
import { StanleyQueryCreditsExpiredError } from './errors';
import {
  StanleyQueryCreditsBalance,
  StanleyQueryCreditsData,
  StanleyQueryCreditsResponse,
  StanleyQueryCreditsScopeItem,
  StanleyQueryHistoryItemResponse,
  StanleyQueryHistoryListResponse,
  StanleyQueryRequestPayload,
  StanleyQueryResponse,
} from './types';

const mapCreditsBalanceRemaining = (
  balance: StanleyQueryCreditsBalance
): number | null => {
  if (balance.is_free) {
    return null;
  }

  if (typeof balance.credits_remaining === 'number') {
    return Math.max(balance.credits_remaining, 0);
  }

  if (balance.credits != null) {
    return Math.max(balance.credits - Number(balance.credits_used ?? 0), 0);
  }

  return null;
};

export const findStanleyQueryCreditsByScope = (
  items: StanleyQueryCreditsResponse,
  scope: string
): StanleyQueryCreditsScopeItem | undefined =>
  items.find((item) => item.scope === scope);

export const mapStanleyQueryCreditsData = (
  item: StanleyQueryCreditsScopeItem
): StanleyQueryCreditsData => ({
  remaining: mapCreditsBalanceRemaining(item.company),
  isFree: Boolean(item.company.is_free),
});

export const mapStanleyQueryCreditsDataForScope = (
  items: StanleyQueryCreditsResponse,
  scope: string
): StanleyQueryCreditsData => {
  const item = findStanleyQueryCreditsByScope(items, scope);

  if (!item) return { remaining: 0, isFree: false };

  return mapStanleyQueryCreditsData(item);
};

export const mapStanleyQueryCreditsRemaining = (
  item: StanleyQueryCreditsScopeItem
): number | null => mapStanleyQueryCreditsData(item).remaining;

export const postStanleyQuerySuggestions = async (
  payload: StanleyQueryRequestPayload
): Promise<StanleyQueryResponse> => {
  const scope = getProductScope();
  const updatedPayload = {
    ...payload,
    scope,
  };
  const url = new URL(STANLEY_QUERY_BUILDER_URL);
  url.searchParams.set('scope', String(scope));

  const response = await fetch(url.href, {
    method: 'POST',
    headers: {
      ...getAuthHeadersWithBearer(),
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(updatedPayload),
  });

  const res = await response.json().catch(() => ({}));

  if (response.status === 402) {
    throw new StanleyQueryCreditsExpiredError();
  }

  if (!response.ok || res.error) {
    throw new Error(res.message || 'Invalid request');
  }

  return res as StanleyQueryResponse;
};

export const fetchStanleyQueryCredits =
  async (): Promise<StanleyQueryCreditsData> => {
    const scope = getProductScope();
    const url = `${STANLEY_QUERY_BUILDER_URL}/credits`;
    const { data } = await fetchWrapper_v2(url, 'GET', undefined, true);

    return mapStanleyQueryCreditsDataForScope(
      data as StanleyQueryCreditsResponse,
      scope
    );
  };

export const fetchStanleyQueryHistoryList =
  async (): Promise<StanleyQueryHistoryListResponse> => {
    const url = `${STANLEY_QUERY_BUILDER_URL}/history`;
    const { data } = await fetchWrapper_v2(url, 'GET', undefined, true);
    return data as StanleyQueryHistoryListResponse;
  };

export const fetchStanleyQueryHistoryItem = async (
  runId: string
): Promise<StanleyQueryHistoryItemResponse> => {
  const url = `${STANLEY_QUERY_BUILDER_URL}/item?run_id=${encodeURIComponent(
    runId
  )}`;
  const { data } = await fetchWrapper_v2(url, 'GET', undefined, true);
  return data as StanleyQueryHistoryItemResponse;
};

export const deleteStanleyQueryHistoryItem = async (params: {
  run_id: string;
  title: string;
}): Promise<void> => {
  const scope = getProductScope();
  const qs = new URLSearchParams({
    scope: String(scope),
    run_id: params.run_id,
    title: params.title,
  }).toString();
  const url = `${STANLEY_QUERY_BUILDER_URL}/item?${qs}`;

  const response = await fetch(url, {
    method: 'DELETE',
    headers: {
      ...getAuthHeadersWithBearer(),
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(text || `Delete failed (${response.status})`);
  }
};

export const renameStanleyQueryHistoryItem = async (params: {
  run_id: string;
  title: string;
}): Promise<void> => {
  const scope = getProductScope();
  const qs = new URLSearchParams({
    scope: String(scope),
    run_id: params.run_id,
    title: params.title,
  }).toString();
  const url = `${STANLEY_QUERY_BUILDER_URL}/item?${qs}`;

  const response = await fetch(url, {
    method: 'PATCH',
    headers: {
      ...getAuthHeadersWithBearer(),
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(text || `Rename failed (${response.status})`);
  }
};

export const fetchStanleyQueryMembershipSize = async (
  body: Record<string, unknown>
): Promise<number> => {
  const result = (await fetchMembershipApi(body)) as {
    error?: unknown;
    response?: { data?: { target_audience_size?: number | string } };
  };
  if (result.error) {
    throw result.error;
  }
  const size = result.response?.data?.target_audience_size;
  if (size === null || Number.isNaN(Number(size))) {
    throw new Error('Missing target_audience_size');
  }
  return Number(size);
};
